import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Sparkles,
  GitCompareArrows,
  Check,
  Pencil,
  X,
  FileText,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { getDocument, getDocumentDownloadUrl } from "@/lib/api/documents";
import {
  listProvisionsByDocument,
  reviewProvision,
  type ProvisionRow,
  type ReviewStatus,
} from "@/lib/api/provisions";
import { runExtraction, type ExtractionRunSummary } from "@/lib/pipeline/run-extraction";
import { enforceMfnForFund, describeMfnDelta } from "@/lib/pipeline/continuous-mfn";
import { useRole } from "@/lib/auth/use-role";
import { useAuth } from "@/lib/auth/auth-provider";
import {
  CATEGORY_LABELS,
  PROVISION_CATEGORIES,
  type ProvisionCategory,
  type StructuredValue,
} from "@argus/engine";
import { Button, Badge, Card, ConfidenceChip, Dot } from "@/components/ui/primitives";
import { fmtDate, fmtUsd } from "@/lib/format";

// ---------------------------------------------------------------------------
// Live extraction progress — staged UI over the (atomic) pipeline call so the
// demo never shows a dead spinner. Phases advance on a timer while pending.
// ---------------------------------------------------------------------------

const PHASES = [
  "Reading document text",
  "Classifying provisions into the taxonomy",
  "Parsing fees, carry, caps & thresholds",
  "Cross-referencing MFN clauses",
  "Calendaring obligations",
];

function ExtractionProgress({ engine }: { engine: string | null }) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStage((s) => Math.min(s + 1, PHASES.length - 1)), 850);
    return () => clearInterval(t);
  }, []);
  return (
    <Card className="border-brand-800/40 bg-brand-950/10">
      <div className="space-y-2.5 p-4">
        <div className="flex items-center gap-2 text-sm font-medium text-neutral-100">
          <Loader2 className="text-brand-400 size-4 animate-spin" />
          {engine === "claude" ? "Claude is reading the document…" : "Extracting provisions…"}
        </div>
        <ul className="space-y-1.5">
          {PHASES.map((p, i) => (
            <li
              key={p}
              className={`flex items-center gap-2 text-xs transition ${
                i < stage
                  ? "text-neutral-400"
                  : i === stage
                    ? "text-neutral-100"
                    : "text-neutral-600"
              }`}
            >
              {i < stage ? (
                <Check className="size-3.5 text-emerald-400" />
              ) : i === stage ? (
                <Loader2 className="text-brand-400 size-3.5 animate-spin" />
              ) : (
                <span className="size-3.5" />
              )}
              {p}
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Document text with provision highlights (bidirectional click-to-source)
// ---------------------------------------------------------------------------

function HighlightedText({
  text,
  provisions,
  selectedId,
  onSelectMark,
}: {
  text: string;
  provisions: ProvisionRow[];
  selectedId: string | null;
  onSelectMark: (id: string) => void;
}) {
  const segments = useMemo(() => {
    const marks = provisions
      .map((p) => ({ id: p.id, start: text.indexOf(p.raw_text), len: p.raw_text.length }))
      .filter((m) => m.start >= 0)
      .sort((a, b) => a.start - b.start);

    const out: { key: string; content: string; markId: string | null }[] = [];
    let cursor = 0;
    for (const m of marks) {
      if (m.start < cursor) continue;
      if (m.start > cursor) {
        out.push({ key: `plain-${cursor}`, content: text.slice(cursor, m.start), markId: null });
      }
      out.push({
        key: `mark-${m.id}`,
        content: text.slice(m.start, m.start + m.len),
        markId: m.id,
      });
      cursor = m.start + m.len;
    }
    if (cursor < text.length) {
      out.push({ key: `plain-${cursor}`, content: text.slice(cursor), markId: null });
    }
    return out;
  }, [text, provisions]);

  return (
    <pre className="whitespace-pre-wrap font-mono text-[12px] leading-relaxed text-neutral-400">
      {segments.map((s) => {
        const mid = s.markId;
        if (!mid) {
          return (
            <span key={s.key}>{s.content.replaceAll("\f", "\n────── page break ──────\n")}</span>
          );
        }
        return (
          <span
            key={s.key}
            id={`prov-${mid}`}
            role="button"
            tabIndex={0}
            onClick={() => onSelectMark(mid)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelectMark(mid);
              }
            }}
            className={`cursor-pointer rounded-sm px-0.5 transition ${
              mid === selectedId
                ? "bg-amber-400/30 text-amber-100 ring-1 ring-amber-400"
                : "bg-brand-400/15 text-brand-100 hover:bg-brand-400/25"
            }`}
          >
            {s.content}
          </span>
        );
      })}
    </pre>
  );
}

// ---------------------------------------------------------------------------
// Structured value editor — labeled fields only, never raw JSON
// ---------------------------------------------------------------------------

function StructuredValueFields({
  value,
  editing,
  onChange,
}: {
  value: StructuredValue | null;
  editing: boolean;
  onChange: (v: StructuredValue) => void;
}) {
  if (!value || Object.keys(value).length === 0) {
    return <p className="text-[11px] text-neutral-600">No structured parameters parsed.</p>;
  }
  const v = value;

  const numberField = (
    key: "fee_pct" | "carry_pct" | "hurdle_pct" | "cap_usd" | "threshold_usd",
    label: string,
    suffix: string,
  ) =>
    v[key] !== undefined ? (
      <label key={key} className="flex items-center gap-2 text-[11px] text-neutral-400">
        <span className="w-36 shrink-0">{label}</span>
        {editing ? (
          <input
            type="number"
            step="any"
            value={v[key]}
            onChange={(e) => onChange({ ...v, [key]: Number(e.target.value) })}
            className="w-28 rounded border border-neutral-700 bg-neutral-950 px-1.5 py-0.5 text-neutral-200"
          />
        ) : (
          <span className="font-medium tabular-nums text-neutral-100">
            {key.endsWith("usd") ? fmtUsd(Number(v[key]), { compact: false }) : v[key]}
          </span>
        )}
        <span className="text-neutral-600">{suffix}</span>
      </label>
    ) : null;

  return (
    <div className="space-y-1 rounded-md bg-neutral-950/40 p-2">
      {numberField("fee_pct", "Management fee", "% p.a.")}
      {numberField("carry_pct", "Carried interest", "%")}
      {numberField("hurdle_pct", "Preferred return", "%")}
      {numberField("cap_usd", "Cap", "USD")}
      {numberField("threshold_usd", "Threshold", "USD")}
      {v.frequency ? (
        <label className="flex items-center gap-2 text-[11px] text-neutral-400">
          <span className="w-36 shrink-0">Frequency</span>
          {editing ? (
            <select
              value={v.frequency}
              onChange={(e) =>
                onChange({
                  ...v,
                  frequency: e.target.value as NonNullable<StructuredValue["frequency"]>,
                })
              }
              className="rounded border border-neutral-700 bg-neutral-950 px-1.5 py-0.5 text-neutral-200"
            >
              {["one_time", "quarterly", "semi_annual", "annual", "on_event"].map((f) => (
                <option key={f} value={f}>
                  {f.replaceAll("_", "-")}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-neutral-100">{v.frequency.replaceAll("_", "-")}</span>
          )}
        </label>
      ) : null}
      {v.mfn_scope ? (
        <div className="flex items-center gap-2 text-[11px] text-neutral-400">
          <span className="w-36 shrink-0">MFN scope</span>
          <span className="text-neutral-100">
            {v.mfn_scope === "economic_only" ? "Economic terms only" : v.mfn_scope}
            {v.mfn_size_based ? " · size-based" : ""}
          </span>
        </div>
      ) : null}
      {v.mfn_excluded_categories?.length ? (
        <div className="flex items-start gap-2 text-[11px] text-neutral-400">
          <span className="w-36 shrink-0">MFN exclusions</span>
          <span className="flex flex-wrap gap-1">
            {v.mfn_excluded_categories.map((c) => (
              <span key={c} className="rounded bg-neutral-800 px-1.5 py-0.5 text-neutral-300">
                {CATEGORY_LABELS[c]}
              </span>
            ))}
          </span>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Provision card
// ---------------------------------------------------------------------------

function reviewTone(s: ReviewStatus): "clear" | "risk" | "breach" | "neutral" {
  if (s === "confirmed" || s === "edited") return "clear";
  if (s === "pending") return "risk";
  if (s === "rejected") return "breach";
  return "neutral";
}

export function ProvisionCard({
  provision,
  canReview,
  selected,
  onSelect,
  onReview,
  busy,
}: {
  provision: ProvisionRow;
  canReview: boolean;
  selected: boolean;
  onSelect: () => void;
  onReview: (patch: {
    review_status: ReviewStatus;
    category?: ProvisionCategory;
    summary?: string;
    structured_value?: StructuredValue | null;
  }) => void;
  busy: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [category, setCategory] = useState<ProvisionCategory>(provision.category);
  const [summary, setSummary] = useState(provision.summary ?? "");
  const [structured, setStructured] = useState<StructuredValue | null>(provision.structured_value);

  const confidence = Number(provision.confidence ?? 0);
  const lowConfidence = confidence < 0.6;
  const reviewed = provision.review_status === "confirmed" || provision.review_status === "edited";

  return (
    <div
      className={`scroll-mt-4 rounded-lg border p-3 transition ${
        selected
          ? "border-amber-500/50 bg-neutral-900 ring-1 ring-amber-500/30"
          : "border-neutral-800 bg-neutral-900/50 hover:border-neutral-700"
      } ${lowConfidence && provision.review_status === "pending" ? "ring-1 ring-red-900/70" : ""}`}
    >
      <button type="button" onClick={onSelect} className="flex w-full items-center gap-2 text-left">
        <span className="text-sm font-medium text-neutral-100">{CATEGORY_LABELS[category]}</span>
        <Badge tone={reviewTone(provision.review_status)} className="capitalize">
          {reviewed ? (
            <Check className="size-2.5" />
          ) : (
            <Dot tone={reviewTone(provision.review_status)} />
          )}
          {provision.review_status}
        </Badge>
        <span className="ml-auto flex items-center gap-2">
          <span className="text-[10px] tabular-nums text-neutral-500">
            p.{provision.page_number ?? "?"}
          </span>
          <ConfidenceChip value={confidence} />
        </span>
      </button>

      {provision.review_status === "pending" && (
        <p className="mt-1.5 flex items-center gap-1 text-[10px] text-amber-500/90">
          <AlertCircle className="size-3" /> Pending review — not yet feeding MFN or the calendar
        </p>
      )}

      {editing ? (
        <div className="mt-2 space-y-2">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as ProvisionCategory)}
            className="w-full rounded border border-neutral-700 bg-neutral-950 px-2 py-1 text-xs text-neutral-200"
          >
            {PROVISION_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={2}
            className="w-full rounded border border-neutral-700 bg-neutral-950 px-2 py-1 text-xs text-neutral-200"
          />
        </div>
      ) : (
        <p className="mt-1.5 text-xs leading-relaxed text-neutral-300">{provision.summary}</p>
      )}

      <div className="mt-2">
        <StructuredValueFields
          value={editing ? structured : provision.structured_value}
          editing={editing}
          onChange={setStructured}
        />
      </div>

      {canReview ? (
        <div className="mt-2.5 flex gap-2">
          {editing ? (
            <>
              <Button
                size="sm"
                variant="success"
                disabled={busy}
                onClick={() => {
                  onReview({
                    review_status: "edited",
                    category,
                    summary,
                    structured_value: structured,
                  });
                  setEditing(false);
                }}
              >
                <Check className="size-3.5" /> Save & confirm
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              {!reviewed && (
                <Button
                  size="sm"
                  variant="success"
                  disabled={busy}
                  onClick={() => onReview({ review_status: "confirmed" })}
                >
                  <Check className="size-3.5" /> Confirm
                </Button>
              )}
              <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                <Pencil className="size-3.5" /> Edit
              </Button>
              {provision.review_status !== "rejected" && (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={busy}
                  onClick={() => onReview({ review_status: "rejected" })}
                >
                  <X className="size-3.5" /> Reject
                </Button>
              )}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function DocumentReviewInner() {
  const tenant = useTenant();
  const { user } = useAuth();
  const params = useParams({ strict: false });
  const documentId = params.documentId ?? "";
  const qc = useQueryClient();
  const { canWrite, role } = useRole(tenant.id);
  const canReview = canWrite || role === "reviewer";

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastSummary, setLastSummary] = useState<ExtractionRunSummary | null>(null);

  const {
    data: doc,
    isLoading: docLoading,
    error: docError,
  } = useQuery({
    queryKey: ["document", documentId],
    queryFn: () => getDocument(documentId),
    enabled: !!documentId,
  });
  const { data: provisions } = useQuery({
    queryKey: ["provisions", documentId],
    queryFn: () => listProvisionsByDocument(documentId),
    enabled: !!doc,
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["document", documentId] });
    void qc.invalidateQueries({ queryKey: ["provisions", documentId] });
    void qc.invalidateQueries({ queryKey: ["documents", tenant.id] });
    void qc.invalidateQueries({ queryKey: ["obligations", tenant.id] });
    void qc.invalidateQueries({ queryKey: ["provision-counts", tenant.id] });
    void qc.invalidateQueries({ queryKey: ["mfn-triggers", tenant.id] });
    void qc.invalidateQueries({ queryKey: ["portfolio-radar", tenant.id] });
  };

  const extract = useMutation({
    mutationFn: () => runExtraction(documentId),
    onSuccess: (s) => {
      setLastSummary(s);
      toast.success(`Extraction complete · ${s.engine === "claude" ? "Claude" : "rules engine"}`, {
        description: `${s.provisionCount} provisions, ${s.mfnClauseCount} MFN clause(s), ${s.obligationCount} obligation(s) calendared.`,
      });
    },
    onError: (e) => toast.error("Extraction failed", { description: e.message }),
    onSettled: invalidate,
  });

  const mfn = useMutation({
    mutationFn: () => {
      if (!doc) throw new Error("Document not loaded.");
      return enforceMfnForFund({
        tenantId: tenant.id,
        fundId: doc.fund_id,
        source: "manual",
        sourceRef: doc.id,
        userId: user?.id,
      });
    },
    onSuccess: (s) => {
      if (!s) {
        toast.error("MFN evaluation failed", {
          description: "Could not complete — check your connection and try again.",
        });
        return;
      }
      const extra = [
        s.newlyStale > 0 ? `${s.newlyStale} flagged stale` : null,
        s.revived > 0 ? `${s.revived} revived` : null,
      ]
        .filter(Boolean)
        .join(", ");
      toast.success("MFN evaluation complete", {
        description: `${s.computedTriggers} trigger(s) across ${s.clauseCount} clause(s); ${s.newTriggers} new${extra ? `, ${extra}` : ""}.`,
      });
    },
    onError: (e) => toast.error("MFN evaluation failed", { description: e.message }),
    onSettled: invalidate,
  });

  const review = useMutation({
    mutationFn: (args: { id: string; patch: Parameters<typeof reviewProvision>[2] }) => {
      if (!user) throw new Error("Not signed in.");
      return reviewProvision(args.id, user.id, args.patch);
    },
    onSuccess: (row) => {
      // Continuous MFN enforcement — same seam as review-queue.tsx. Silent
      // (no toast) unless something actually changed. 2026-07-03 audit fix:
      // enforceMfnForFund can now throw on a genuine core-step failure — log
      // it instead of swallowing to `undefined` a second time, so a real
      // failure still leaves a trace even though the review itself must not
      // be blocked by it.
      enforceMfnForFund({
        tenantId: tenant.id,
        fundId: row.fund_id,
        source: "provision_reviewed",
        sourceRef: row.id,
        userId: user?.id,
      })
        .then((summary) => {
          const delta = describeMfnDelta(summary);
          if (delta) toast.success("MFN re-evaluated", { description: delta });
        })
        .catch((err: unknown) => {
          console.error(
            `[document-review] continuous MFN enforcement failed for fund=${row.fund_id}:`,
            err,
          );
        });
    },
    onSettled: invalidate,
  });

  const download = useMutation({
    mutationFn: async () => {
      if (!doc) throw new Error("Document not loaded.");
      const url = await getDocumentDownloadUrl(doc.storage_path);
      window.open(url, "_blank", "noopener");
    },
    onError: (e) => toast.error("Download failed", { description: e.message }),
  });

  const selectProvision = (id: string) => {
    setSelectedId(id);
    document.getElementById(`prov-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    document.getElementById(`card-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (docLoading) return <p className="text-sm text-neutral-500">Loading document…</p>;
  if (docError || !doc) {
    return (
      <div className="rounded-lg border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
        {docError?.message ?? "Document not found."}
      </div>
    );
  }

  const pendingCount = provisions?.filter((p) => p.review_status === "pending").length ?? 0;
  const confirmedCount =
    provisions?.filter((p) => p.review_status === "confirmed" || p.review_status === "edited")
      .length ?? 0;

  return (
    <>
      <div>
        <Link
          to="/t/$slug/documents"
          params={{ slug: tenant.slug }}
          className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-300"
        >
          <ArrowLeft className="size-3.5" /> Documents
        </Link>
      </div>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-50">{doc.title}</h1>
          <div className="flex flex-wrap items-center gap-2 text-sm text-neutral-400">
            <span className="capitalize">{doc.document_type.replaceAll("_", " ")}</span>
            <span className="text-neutral-700">·</span>
            <Badge
              tone={
                doc.status === "extracted"
                  ? "clear"
                  : doc.status === "failed"
                    ? "breach"
                    : "neutral"
              }
            >
              {doc.status}
            </Badge>
            {doc.executed_date && (
              <>
                <span className="text-neutral-700">·</span>
                <span>executed {fmtDate(doc.executed_date)}</span>
              </>
            )}
            {provisions && provisions.length > 0 && (
              <>
                <span className="text-neutral-700">·</span>
                <span className="tabular-nums">
                  {provisions.length} provisions · {confirmedCount} confirmed · {pendingCount}{" "}
                  pending
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => download.mutate()}>
            <Download className="size-3.5" /> Original
          </Button>
          {canReview && (
            <>
              <Button
                size="sm"
                variant="primary"
                onClick={() => extract.mutate()}
                disabled={extract.isPending}
              >
                <Sparkles className="size-3.5" />
                {extract.isPending
                  ? "Extracting…"
                  : doc.status === "extracted"
                    ? "Re-run extraction"
                    : "Run extraction"}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => mfn.mutate()}
                disabled={mfn.isPending || doc.status !== "extracted"}
                title={doc.status !== "extracted" ? "Run extraction first" : ""}
              >
                <GitCompareArrows className="size-3.5" />
                {mfn.isPending ? "Evaluating…" : "Evaluate MFN"}
              </Button>
            </>
          )}
        </div>
      </header>

      {extract.isPending && <ExtractionProgress engine={lastSummary?.engine ?? null} />}

      {lastSummary && !extract.isPending && lastSummary.warnings.length > 0 && (
        <div className="rounded-lg border border-amber-900/60 bg-amber-950/20 px-3 py-2 text-xs text-amber-300">
          {lastSummary.warnings.join(" ")} Review each provision — nothing is authoritative until a
          human confirms it.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="max-h-[72vh] overflow-y-auto">
          <div className="sticky top-0 z-10 flex items-center gap-1.5 border-b border-neutral-800/70 bg-neutral-900/80 px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider text-neutral-500 backdrop-blur">
            <FileText className="size-3.5" /> Source document
          </div>
          <div className="p-4">
            {doc.extracted_text ? (
              <HighlightedText
                text={doc.extracted_text}
                provisions={provisions ?? []}
                selectedId={selectedId}
                onSelectMark={selectProvision}
              />
            ) : (
              <p className="text-sm text-neutral-500">
                No machine-readable text — this file needs OCR (Textract integration, Phase 2).
              </p>
            )}
          </div>
        </Card>

        <section className="max-h-[72vh] space-y-2 overflow-y-auto pr-1">
          <h2 className="px-1 text-[11px] font-medium uppercase tracking-wider text-neutral-500">
            Extracted provisions
          </h2>
          {!provisions || provisions.length === 0 ? (
            <Card className="p-6 text-center text-sm text-neutral-400">
              {doc.status === "pending"
                ? "No provisions yet — run extraction to populate this panel."
                : "No provisions extracted."}
            </Card>
          ) : (
            provisions.map((p) => (
              <div id={`card-${p.id}`} key={p.id}>
                <ProvisionCard
                  provision={p}
                  canReview={canReview}
                  selected={selectedId === p.id}
                  busy={review.isPending}
                  onSelect={() => selectProvision(p.id)}
                  onReview={(patch) => review.mutate({ id: p.id, patch })}
                />
              </div>
            ))
          )}
        </section>
      </div>
    </>
  );
}

export function DocumentReviewPage() {
  return (
    <TenantLayout>
      <DocumentReviewInner />
    </TenantLayout>
  );
}
